/**
 * Flow mutations of the Voluum flow API shape (path toggle, item weights,
 * path weights), for any tracker that serves it: Voluum at /voluum/flows,
 * Tres Commas (a Voluum-API clone) at /tres-commas/flows. Each tracker's
 * hooks pass their own base and flow query key; the requests are the ones
 * the Voluum hooks always sent.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';

import api from '@/services/api';

export interface FlowMutationTarget {
  /** e.g. '/voluum/flows' or '/tres-commas/flows'. */
  base: string;
  /** Query key of one flow, invalidated after a write. */
  flowKey: (flowId: string) => QueryKey;
}

type WriteResult = { success: true; message: string };
type WeightUpdate = { itemId: string; weight: number };

function useFlowWrite<P extends { flowId: string }>(
  target: FlowMutationTarget,
  send: (params: P) => Promise<{ data: { data: unknown } }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: P) => (await send(params)).data.data as WriteResult,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: target.flowKey(variables.flowId) });
    },
  });
}

export function useFlowPathToggle(target: FlowMutationTarget) {
  return useFlowWrite(target, (p: { flowId: string; pathId: string; active: boolean }) =>
    api.post(`${target.base}/${p.flowId}/paths/${p.pathId}/toggle`, { active: p.active }),
  );
}

export function useFlowItemWeights(target: FlowMutationTarget) {
  return useFlowWrite(
    target,
    (p: {
      flowId: string;
      pathId: string;
      type: 'landers' | 'offers';
      updates: WeightUpdate[];
    }) =>
      api.put(`${target.base}/${p.flowId}/items/weights`, {
        pathId: p.pathId,
        type: p.type,
        updates: p.updates.map((u) => ({ itemId: u.itemId, weight: u.weight })),
      }),
  );
}

export function useFlowPathWeights(target: FlowMutationTarget) {
  return useFlowWrite(target, (p: { flowId: string; updates: WeightUpdate[] }) =>
    api.put(`${target.base}/${p.flowId}/paths/weights`, {
      updates: p.updates.map((u) => ({ itemId: u.itemId, weight: u.weight })),
    }),
  );
}
